import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { gbp } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ClipboardList, RefreshCw, AlertTriangle, ChevronRight, CheckCircle2 } from "lucide-react";

function Section({ Icon, ring, title, count, children }) {
  return (
    <Card className="p-5 rounded-2xl border-border" data-testid={`na-section-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className="flex items-center gap-3 mb-4">
        <span className={`w-10 h-10 rounded-xl grid place-items-center ${ring}`}><Icon className="w-5 h-5" /></span>
        <h2 className="font-alt font-bold text-cass-navy text-lg">{title}</h2>
        <Badge variant="secondary" className="ml-1 tabular">{count}</Badge>
      </div>
      {children}
    </Card>
  );
}

export default function NeedsAttention() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [unsynced, setUnsynced] = useState([]);
  const [rejected, setRejected] = useState([]);
  const [expired, setExpired] = useState([]);
  const [syncingAll, setSyncingAll] = useState(false);

  const loadUnsynced = () => api.get("/dashboard/unsynced").then((r) => setUnsynced(r.data || [])).catch(() => {});
  useEffect(() => {
    api.get("/engager/my-tasks").then((r) => setTasks(r.data || [])).catch(() => {});
    loadUnsynced();
    api.get("/proposals", { params: { status: "rejected" } }).then((r) => setRejected(r.data || [])).catch(() => {});
    api.get("/proposals", { params: { status: "expired" } }).then((r) => setExpired(r.data || [])).catch(() => {});
  }, []);

  const openTasks = tasks.filter((t) => !t.completed);
  const rejExp = [...rejected, ...expired];

  const toggleTask = async (t, completed) => {
    setTasks((ts) => ts.map((x) => (x.task_id === t.task_id && x.setup_id === t.setup_id ? { ...x, completed } : x)));
    try { await api.post(`/engager-setups/${t.setup_id}/toggle`, { task_id: t.task_id, completed }); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
  };

  const syncAll = async () => {
    setSyncingAll(true);
    await Promise.all(unsynced.map((p) => api.post(`/xero/sync/${p.id}`).catch(() => {})));
    await loadUnsynced();
    setSyncingAll(false);
  };

  return (
    <div className="p-6 md:p-8 space-y-5" data-testid="needs-attention-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl font-bold text-cass-navy leading-none">Needs attention</h1>
          <p className="text-cass-slate mt-1">Everything that needs an action, in one place.</p>
        </div>
        <Button variant="outline" onClick={() => navigate("/dashboard")} data-testid="na-back-dashboard">← Dashboard</Button>
      </div>

      {/* My Tasks */}
      <Section Icon={ClipboardList} ring="bg-cass-teal/10 text-cass-teal" title="My tasks" count={openTasks.length}>
        {openTasks.length === 0 ? (
          <p className="text-sm text-cass-slate italic py-2 flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-cass-teal" /> All caught up.</p>
        ) : (
          <div className="divide-y divide-border">
            {openTasks.map((t) => (
              <div key={`${t.setup_id}-${t.task_id}`} data-testid={`na-task-${t.task_id}`} className="flex items-center gap-3 py-3">
                <button onClick={() => toggleTask(t, true)} data-testid={`na-task-done-${t.task_id}`} className="w-5 h-5 rounded border border-slate-300 hover:border-cass-teal shrink-0" title="Mark complete" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-cass-navy">{t.title}</div>
                  <div className="text-xs text-cass-slate truncate">{t.client_name} · {t.proposal_ref} · {t.service_name}</div>
                </div>
                {t.due_date && <Badge variant={t.overdue ? "destructive" : "outline"} className="shrink-0">{t.overdue && <AlertTriangle className="w-3 h-3 mr-1" />}due {new Date(t.due_date).toLocaleDateString("en-GB")}</Badge>}
              </div>
            ))}
          </div>
        )}
        <button onClick={() => navigate("/engager/my-tasks")} className="text-sm text-cass-teal hover:underline mt-3 inline-block" data-testid="na-view-all-tasks">Open My Tasks →</button>
      </Section>

      {/* Not synced to Xero */}
      <Section Icon={RefreshCw} ring="bg-amber-50 text-amber-500" title="Not synced to Xero" count={unsynced.length}>
        {unsynced.length === 0 ? (
          <p className="text-sm text-cass-slate italic py-2 flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> All accepted proposals are synced.</p>
        ) : (
          <>
            <div className="flex justify-end mb-2"><Button size="sm" onClick={syncAll} disabled={syncingAll} data-testid="na-sync-all-btn">{syncingAll ? "Syncing…" : "Sync all"}</Button></div>
            <div className="space-y-1.5">
              {unsynced.map((p) => (
                <div key={p.id} data-testid={`na-unsynced-${p.id}`} onClick={() => navigate(`/proposals/${p.id}`)} className="flex items-center justify-between p-2.5 rounded-md border border-border hover:bg-cass-bone cursor-pointer">
                  <div className="text-sm text-cass-navy">{p.reference} · <span className="text-cass-slate">{p.client_name}</span></div>
                  <Badge variant="outline" className="text-amber-600 border-amber-300">Not synced</Badge>
                </div>
              ))}
            </div>
          </>
        )}
      </Section>

      {/* Rejected / expired */}
      <Section Icon={AlertTriangle} ring="bg-rose-50 text-rose-500" title="Rejected / expired" count={rejExp.length}>
        {rejExp.length === 0 ? (
          <p className="text-sm text-cass-slate italic py-2 flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> Nothing rejected or expired.</p>
        ) : (
          <div className="space-y-1.5">
            {rejExp.map((p) => (
              <div key={p.id} data-testid={`na-rejexp-${p.id}`} onClick={() => navigate(`/proposals/${p.id}`)} className="flex items-center justify-between p-2.5 rounded-md border border-border hover:bg-cass-bone cursor-pointer">
                <div><div className="text-sm font-medium text-cass-navy">{p.reference}</div><div className="text-xs text-cass-slate">{p.client_name} · {gbp(p.totals?.monthly_recurring)}/mo</div></div>
                <div className="flex items-center gap-2"><Badge variant={p.status === "rejected" ? "destructive" : "secondary"} className="capitalize">{p.status}</Badge><ChevronRight className="w-4 h-4 text-slate-300" /></div>
              </div>
            ))}
          </div>
        )}
        <button onClick={() => navigate("/proposals")} className="text-sm text-cass-teal hover:underline mt-3 inline-block" data-testid="na-view-all-proposals">Open Proposals →</button>
      </Section>
    </div>
  );
}
